import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Icon } from '@/components/synapse/Icon';
import { useHasPermission } from '@/hooks/usePermissions';
import { translateApiError } from '@/lib/apiError';
import { useAptTickets, useIssueTicket } from './useAptTickets';

interface QueueNumberButtonProps {
  patientId: string;
}

/**
 * The front desk's "give this patient a number" control, for a patient pane.
 *
 * Shows the number instead of the button once the patient holds one today —
 * the receptionist's next question after pressing it is "which number?", and
 * the same pane reopened an hour later should still answer it.
 *
 * Renders nothing for a member who cannot read the counter; a member who can
 * read but not manage sees a number that exists and no button to make one.
 */
export function QueueNumberButton({ patientId }: QueueNumberButtonProps) {
  const { t } = useTranslation();
  const canRead = useHasPermission('apt_counter.read');
  const canManage = useHasPermission('apt_counter.manage');
  const { data, isLoading } = useAptTickets();
  const issue = useIssueTicket();

  if (!canRead) return null;

  const ticket = data?.tickets.find((x) => x.patient.id === patientId && x.status === 'waiting');

  if (ticket) {
    return (
      <div
        className="inline-flex h-10 items-center gap-2 rounded-md border border-line-2 bg-paper-2 px-3.5"
        aria-label={t('aptCounter.numberToday', { number: ticket.number })}
      >
        <Icon name="calendar" size={16} className="text-muted" />
        <span className="text-[12.5px] text-muted">{t('aptCounter.queueNumber')}</span>
        <span className="font-mono text-[18px] font-bold tabular-nums leading-none">
          {ticket.number}
        </span>
      </div>
    );
  }

  if (!canManage) return null;

  return (
    <div className="flex flex-col gap-1.5">
      <Button
        onClick={() => issue.mutate(patientId)}
        // Until the listing has loaded we cannot tell "has no number" from "not
        // known yet", and a press in that window would be a guaranteed 409 for
        // a patient who already holds one.
        disabled={isLoading || issue.isPending}
      >
        <Icon name="plus" size={16} />
        {issue.isPending ? t('aptCounter.givingNumber') : t('aptCounter.giveNumber')}
      </Button>
      {issue.isError && (
        <span role="alert" className="text-[12.5px] text-destructive">
          {translateApiError(issue.error)}
        </span>
      )}
    </div>
  );
}
